import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, normalizeLanguage } from '@atlas/shared';
import en from '../locales/en/translation.json';

const STORAGE_KEY = 'atlas-language';
const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

// Vite maps every locale file for on-demand import. en is bundled separately.
const localeLoaders = import.meta.glob('../locales/*/translation.json');

const loaded = new Set<string>(['en']);

/** localStorage → navigator language → default. Only returns supported codes. */
export function resolveInitialLanguage(): string {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_CODES.includes(stored)) return stored;
    const nav = normalizeLanguage(window.navigator.language ?? '');
    if (SUPPORTED_CODES.includes(nav)) return nav;
  }
  return DEFAULT_LANGUAGE;
}

/** Dynamically import + register a locale bundle. No-op for en or unknown codes. */
export async function loadLocale(code: string): Promise<void> {
  if (loaded.has(code)) return;
  const loader = localeLoaders[`../locales/${code}/translation.json`];
  if (!loader) return; // missing bundle → fallbackLng 'en'
  const mod = (await loader()) as { default: Record<string, unknown> };
  i18n.addResourceBundle(code, 'translation', mod.default, true, true);
  loaded.add(code);
}

/** Load (if needed), switch i18next, and persist to localStorage. */
export async function setLanguage(code: string): Promise<void> {
  await loadLocale(code);
  await i18n.changeLanguage(code);
  if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, code);
}

/** Initialize i18next with en bundled, then apply the resolved initial language. */
export async function initI18n(): Promise<void> {
  const lng = resolveInitialLanguage();
  if (i18n.isInitialized) {
    if (lng !== 'en') await setLanguage(lng);
    return;
  }
  await i18n.use(initReactI18next).init({
    resources: { en: { translation: en } },
    lng: 'en',
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_CODES,
    interpolation: { escapeValue: false },
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (_lngs: readonly string[], _ns: string, key: string) => {
      if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`);
    },
  });
  if (lng !== 'en') await setLanguage(lng);
}

export default i18n;
